import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Table, Statistic, Button, Row, Col, Space, Tag, Input, Select, DatePicker, message, Tooltip, Modal, Alert, Empty,
  Switch, TimePicker,
} from 'antd';
import {
  ReloadOutlined, DownloadOutlined, ArrowUpOutlined, ArrowDownOutlined,
  WarningOutlined, DatabaseOutlined, SearchOutlined, CopyOutlined,
  BugOutlined, ExclamationCircleOutlined, RiseOutlined, FallOutlined,
  RobotOutlined, HistoryOutlined, CheckCircleOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch } from '../utils/api.js';

const { RangePicker } = DatePicker;

// 数量格式化：整数不带小数点，小数保留必要位
const fmtQty = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '-';
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
};

export default function InventoryLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ todayIn: {}, todayOut: {}, negative: 0, total: 0, sources: [], operators: [] });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  // 负库存诊断
  const [diagOpen, setDiagOpen] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState([]);

  // AI 库存巡检
  const [auditCfg, setAuditCfg] = useState({ enabled: true, hour: 8, minute: 10, pushConfigured: false, pushEnabled: false, lastRunAt: '', lastRunCount: null });
  const [auditLatest, setAuditLatest] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditHistOpen, setAuditHistOpen] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);

  // 筛选条件
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState('');
  const [source, setSource] = useState('');
  const [operator, setOperator] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const buildQuery = useCallback((p, ps) => {
    const q = new URLSearchParams();
    if (keyword.trim()) q.set('keyword', keyword.trim());
    if (type) q.set('type', type);
    if (source) q.set('source', source);
    if (operator) q.set('operator', operator);
    if (dateRange && dateRange[0] && dateRange[1]) {
      q.set('startDate', dateRange[0].format('YYYY-MM-DD'));
      q.set('endDate', dateRange[1].format('YYYY-MM-DD'));
    }
    q.set('page', p || 1);
    q.set('pageSize', ps || pageSize);
    return q.toString();
  }, [keyword, type, source, operator, dateRange, pageSize]);

  const load = useCallback(async (p, ps) => {
    setLoading(true);
    try {
      const d = await apiFetch('/api/inventory-logs?' + buildQuery(p, ps)).then(r => r.json());
      setLogs(d.data || []);
      setTotal(d.total || 0);
      if (d.stats) setStats(d.stats);
    } catch (e) { message.error('流水加载失败'); }
    setLoading(false);
  }, [buildQuery]);

  useEffect(() => { load(1, pageSize); }, [load, pageSize]);

  const applyFilter = () => { setPage(1); load(1, pageSize); };
  const resetFilter = () => {
    setKeyword(''); setType(''); setSource(''); setOperator(''); setDateRange(null);
    setPage(1);
    // 重置后重新加载：等 state 更新后再 load（用空条件手动构造）
    const q = new URLSearchParams({ page: '1', pageSize: String(pageSize) });
    setLoading(true);
    apiFetch('/api/inventory-logs?' + q.toString()).then(r => r.json()).then(d => {
      setLogs(d.data || []); setTotal(d.total || 0); if (d.stats) setStats(d.stats); setLoading(false);
    }).catch(() => setLoading(false));
  };

  const refresh = () => load(page, pageSize);

  // 导出 CSV：按当前筛选条件循环拉全量
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = [];
      let p = 1;
      const ps = 200;
      while (true) {
        const d = await apiFetch('/api/inventory-logs?' + buildQuery(p, ps)).then(r => r.json());
        all.push(...(d.data || []));
        if (!d.data || d.data.length === 0 || all.length >= d.total) break;
        p++;
      }
      if (!all.length) { message.info('无数据可导出'); setExporting(false); return; }
      const esc = (v) => {
        const s = String(v == null ? '' : v);
        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const header = ['时间(北京时间)', '商品编码', '商品名称', '类型', '变动数量', '变动后余额', '来源', '关联单号', '操作人'];
      const rows = all.map(l => [
        l.time, l.spec_code, l.product_name, (l.type === 'in' ? '入库' : '出库'),
        (l.type === 'in' ? '+' : '') + l.qty, l.balance, l.source, l.ref || '', l.operator || '',
      ]);
      const csv = '\uFEFF' + [header, ...rows].map(r => r.map(esc).join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `库存变动记录_${dayjs().format('YYYYMMDD_HHmm')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      message.success(`已导出 ${all.length} 条记录`);
    } catch (e) { message.error('导出失败'); }
    setExporting(false);
  };

  const copyRef = (ref) => {
    if (!ref) return;
    navigator.clipboard && navigator.clipboard.writeText(ref).then(() => message.success('已复制单号'));
  };

  // ===== AI 库存巡检：配置 + 最近一次结果 =====
  const loadAudit = useCallback(async () => {
    try {
      const d = await apiFetch('/api/stock-audit/latest').then(r => r.json());
      if (d && d.success) {
        setAuditLatest(d.data || null);
        if (d.config) setAuditCfg(prev => ({ ...prev, ...d.config }));
      }
    } catch (e) { /* 静默 */ }
    try {
      const c = await apiFetch('/api/stock-audit/config').then(r => r.json());
      if (c && c.success) setAuditCfg(prev => ({ ...prev, ...c.data }));
    } catch (e) { /* 静默 */ }
  }, []);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const saveAuditCfg = async (patch) => {
    const next = { ...auditCfg, ...patch };
    setAuditCfg(next);
    try {
      const r = await apiFetch('/api/stock-audit/config', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next.enabled, hour: next.hour, minute: next.minute }),
      }).then(x => x.json());
      if (r.success) message.success('巡检设置已保存'); else message.error(r.error || '保存失败');
    } catch (e) { message.error('保存失败'); }
  };

  const runAuditNow = async () => {
    setAuditRunning(true);
    try {
      const r = await apiFetch('/api/stock-audit/run', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ push: true }),
      }).then(x => x.json());
      if (!r.success) throw new Error(r.error || '巡检失败');
      setAuditLatest(r.data);
      setAuditOpen(true);
      if (!r.data.count) message.success('巡检完成：库存健康，无负库存');
      else message.warning(`巡检完成：发现 ${r.data.count} 个负库存商品，已推送微信`);
    } catch (e) { message.error(e.message || '巡检失败'); }
    setAuditRunning(false);
  };

  const openAuditHistory = async () => {
    setAuditHistOpen(true);
    setAuditHistory([]);
    try {
      const d = await apiFetch('/api/stock-audit/history?limit=20').then(r => r.json());
      setAuditHistory(d.data || []);
    } catch (e) { message.error('历史加载失败'); }
  };

  // 打开负库存诊断
  const openDiagnosis = async () => {
    setDiagOpen(true);
    setDiagLoading(true);
    setDiagData([]);
    try {
      const d = await apiFetch('/api/inventory/negative-diagnosis').then(r => r.json());
      setDiagData(d.data || []);
    } catch (e) { message.error('诊断失败'); }
    setDiagLoading(false);
  };

  const columns = [
    {
      title: '时间', dataIndex: 'time', width: 168, fixed: 'left',
      sorter: (a, b) => String(a.time).localeCompare(String(b.time)),
      render: (v) => <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#595959' }}>{v}</span>,
    },
    {
      title: '商品编码', dataIndex: 'spec_code', width: 190,
      render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--brand-strong)' }}>{v}</span>,
    },
    { title: '商品名称', dataIndex: 'product_name', ellipsis: true },
    {
      title: '类型', dataIndex: 'type', width: 90, align: 'center',
      render: (v) => v === 'in'
        ? <Tag color="green" icon={<ArrowUpOutlined />} style={{ borderRadius: 4 }}>入库</Tag>
        : <Tag color="red" icon={<ArrowDownOutlined />} style={{ borderRadius: 4 }}>出库</Tag>,
    },
    {
      title: '变动数量', dataIndex: 'qty', width: 120, align: 'right',
      sorter: (a, b) => Number(a.qty) - Number(b.qty),
      render: (v, r) => {
        const isIn = r.type === 'in';
        return <b style={{ color: isIn ? '#3F8600' : '#CF1322', fontSize: 14 }}>{isIn ? '+' : ''}{fmtQty(v)}</b>;
      },
    },
    {
      title: '变动后余额', dataIndex: 'balance', width: 130, align: 'right',
      sorter: (a, b) => Number(a.balance) - Number(b.balance),
      render: (v) => {
        const n = Number(v);
        const neg = n < 0;
        return <span style={neg
          ? { color: '#CF1322', fontWeight: 700, background: '#FFF1F0', padding: '2px 8px', borderRadius: 4 }
          : { fontWeight: 600, color: '#26201C' }}>{fmtQty(v)}</span>;
      },
    },
    {
      title: '来源', dataIndex: 'source', width: 160,
      render: (v) => <Tag color="blue" style={{ borderRadius: 4 }}>{v || '-'}</Tag>,
    },
    {
      title: '关联单号', dataIndex: 'ref', width: 170,
      render: (v) => v
        ? <Space size={2}>
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>
            <Tooltip title="复制"><Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copyRef(v)} /></Tooltip>
          </Space>
        : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    { title: '操作人', dataIndex: 'operator', width: 110, render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
  ];

  // 单个负库存诊断卡（诊断弹窗 & AI 巡检弹窗共用）
  const renderDiagCard = (d, idx) => (
    <div key={d.spec_code} style={{ border: '1px solid #FFD6CF', borderLeft: '4px solid #CF1322', borderRadius: 8, padding: 14, background: '#FFF' }}>
      {/* 头部：名称 + 编码 + 当前库存 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#26201C' }}>
            {idx + 1}. {d.product_name || d.spec_code}
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--brand-strong)', marginTop: 2 }}>{d.spec_code}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>当前库存</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#CF1322' }}>{fmtQty(d.stock)}</div>
        </div>
      </div>

      {/* 根因标签 */}
      <div style={{ marginTop: 10 }}>
        {(d.causes || []).map(c => (
          <Tag key={c} color="red" style={{ borderRadius: 4, marginBottom: 4 }}>{c}</Tag>
        ))}
      </div>

      {/* 首次变负现场 */}
      {d.scene && (
        <div style={{ marginTop: 10, background: '#FFF1F0', borderRadius: 6, padding: '10px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#CF1322', marginBottom: 5 }}>
            <ExclamationCircleOutlined /> 首次变负现场
          </div>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: 2 }}>
            时间：<b style={{ fontFamily: 'monospace' }}>{d.scene.time || '-'}</b>　·　来源：{d.scene.source || '-'}　·　操作人：{d.scene.operator || '-'}<br />
            {d.scene.ref ? <>关联单号：<span style={{ fontFamily: 'monospace' }}>{d.scene.ref}</span><br /></> : null}
            本次扣减：<b style={{ color: '#CF1322' }}>-{fmtQty(Math.abs(d.scene.deduct_qty || 0))}</b>
            （{fmtQty(d.scene.prev_stock)} → <b>{fmtQty(d.scene.new_stock)}</b>）
          </div>
        </div>
      )}

      {/* 累计缺口 */}
      <div style={{ marginTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: '#595959' }}>
        <span><RiseOutlined style={{ color: '#3F8600' }} /> 累计入库 <b>{fmtQty(d.totalIn)}</b></span>
        <span><FallOutlined style={{ color: '#CF1322' }} /> 累计出库 <b>{fmtQty(d.totalOut)}</b></span>
        <span>缺口 <b style={{ color: '#CF1322' }}>{fmtQty(d.gap)}</b></span>
        <span style={{ color: '#8c8c8c' }}>流水 {d.logCount} 条</span>
      </div>

      {/* 疑似错位 */}
      {(d.suspects || []).length > 0 && (
        <div style={{ marginTop: 8 }}>
          {(d.suspects || []).map(s => (
            <Tag key={s.spec_code} color="orange" style={{ borderRadius: 4, marginBottom: 4 }}>
              疑似错位：{s.product_name}（{s.spec_code}）库存 +{fmtQty(s.stock)}
            </Tag>
          ))}
        </div>
      )}

      {/* 售后退回未入库明细 */}
      {(d.pendingReturn || []).length > 0 && (
        <div style={{ marginTop: 10, background: '#FFF7E6', borderRadius: 6, padding: '10px 12px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#FA8C16', marginBottom: 5 }}>
            <ExclamationCircleOutlined /> 售后退回未及时入库（{d.pendingReturn.length} 单 · 共 {fmtQty(d.pendingReturnQty)} 件）
          </div>
          <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.9 }}>
            {(d.pendingReturn || []).map(p => (
              <div key={p.id}>
                · 售后 #{p.id} · 订单 <span style={{ fontFamily: 'monospace' }}>{p.platform_order_no}</span> · {p.type} · 退货 {fmtQty(p.return_quantity)} 件 · 状态 <b style={{ color: '#FA8C16' }}>{p.return_status}</b>
                {p.return_tracking_no ? <> · 退回单号 <span style={{ fontFamily: 'monospace' }}>{p.return_tracking_no}</span></> : null}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 5 }}>
            这些退回件已在仓但未登记「已入库」，导致新订单发货时多扣了库存。到「售后管理」把它们改成「已入库」即会自动补回库存。
          </div>
        </div>
      )}

      {/* 建议 */}
      <div style={{ marginTop: 10, fontSize: 12, color: 'var(--brand)', background: '#FFF7E6', border: '1px solid #FFE4D6', borderRadius: 6, padding: '8px 12px', lineHeight: 1.7 }}>
        💡 {d.suggestion}
      </div>
    </div>
  );

  return (
    <div className="page-container">
      {/* ===== 顶部统计卡 ===== */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #3F8600', borderRadius: 6 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>今日入库（{stats.todayIn?.count || 0} 笔）</span>}
              value={stats.todayIn?.qty || 0}
              valueStyle={{ color: '#3F8600', fontWeight: 600, fontSize: 20 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>+{fmtQty(stats.todayIn?.qty || 0)} 件</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid var(--brand)', borderRadius: 6 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>今日出库（{stats.todayOut?.count || 0} 笔）</span>}
              value={stats.todayOut?.qty || 0}
              valueStyle={{ color: 'var(--brand)', fontWeight: 600, fontSize: 20 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>-{fmtQty(stats.todayOut?.qty || 0)} 件</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: `3px solid ${(stats.negative || 0) > 0 ? '#CF1322' : '#bfbfbf'}`, borderRadius: 6 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>负库存商品</span>}
              value={stats.negative || 0} suffix="个"
              valueStyle={{ color: (stats.negative || 0) > 0 ? '#CF1322' : '#bfbfbf', fontWeight: 600, fontSize: 20 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
              {(stats.negative || 0) > 0 ? '需排查' : '无异常'}
            </div>
            {(stats.negative || 0) > 0 && (
              <Button
                type="primary" danger size="small" icon={<BugOutlined />}
                style={{ marginTop: 8, borderRadius: 4 }}
                onClick={openDiagnosis}
              >
                立即诊断
              </Button>
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderLeft: '3px solid #1890FF', borderRadius: 6 }}>
            <Statistic
              title={<span style={{ fontSize: 12, color: '#8c8c8c' }}>流水总条数</span>}
              value={stats.total || 0} suffix="条"
              valueStyle={{ color: '#1890FF', fontWeight: 600, fontSize: 20 }}
            />
            <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>最近保留 10000 条</div>
          </Card>
        </Col>
      </Row>

      {/* ===== AI 库存巡检 ===== */}
      <Card size="small" style={{ borderRadius: 8, marginBottom: 12, background: '#F6FFED', border: '1px solid #D9F7BE' }}>
        <Row gutter={[12, 10]} align="middle">
          <Col flex="auto">
            <Space size={10} align="start" wrap={false}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: '#3F8600', color: '#fff', fontSize: 17, flexShrink: 0 }}>
                <RobotOutlined />
              </span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#26201C' }}>
                  缇娜·库存巡检
                  {!auditLatest
                    ? <Tag style={{ marginLeft: 8, borderRadius: 4 }}>尚未巡检</Tag>
                    : auditLatest.count > 0
                      ? <Tag color="red" style={{ marginLeft: 8, borderRadius: 4 }}>最近发现 {auditLatest.count} 个负库存</Tag>
                      : <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginLeft: 8, borderRadius: 4 }}>最近一次正常</Tag>}
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 3 }}>
                  {auditCfg.enabled
                    ? `每天 ${String(auditCfg.hour).padStart(2, '0')}:${String(auditCfg.minute).padStart(2, '0')} 自动排查负库存，缇娜定位根因 · 发现问题才推微信`
                    : '每日自动巡检已关闭（仍可手动「立即巡检」）'}
                  {auditLatest?.time ? ` · 最近巡检 ${auditLatest.time}` : ''}
                </div>
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap size={8}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>自动巡检</span>
              <Switch checked={auditCfg.enabled} onChange={v => saveAuditCfg({ enabled: v })} />
              <TimePicker
                format="HH:mm" minuteStep={5} allowClear={false} style={{ width: 86 }}
                value={dayjs().hour(auditCfg.hour || 0).minute(auditCfg.minute || 0)}
                onChange={(t) => { if (t) saveAuditCfg({ hour: t.hour(), minute: t.minute() }); }}
              />
              <Button type="primary" icon={<ThunderboltOutlined />} loading={auditRunning} onClick={runAuditNow}>立即巡检</Button>
              <Button icon={<HistoryOutlined />} onClick={openAuditHistory}>巡检历史</Button>
              {auditLatest?.count > 0 && (
                <Button danger icon={<BugOutlined />} onClick={() => setAuditOpen(true)}>查看最近报告</Button>
              )}
            </Space>
          </Col>
        </Row>
        {!auditCfg.pushConfigured && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#FA8C16' }}>
            ⚠️ 还没配置 PushPlus 微信推送，巡检发现问题不会发到微信（配置后可自动推送）
          </div>
        )}
      </Card>

      {/* ===== 筛选栏 ===== */}
      <Card size="small" style={{ borderRadius: 8, marginBottom: 12, background: '#FFF9F6', border: '1px solid #FFE4D6' }}>
        <Space wrap size={8} style={{ width: '100%' }}>
          <Input
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            placeholder="编码 / 名称"
            allowClear style={{ width: 180 }} value={keyword}
            onChange={e => setKeyword(e.target.value)} onPressEnter={applyFilter}
          />
          <Select placeholder="类型" allowClear style={{ width: 100 }} value={type || undefined}
            onChange={v => setType(v || '')}
            options={[{ value: 'in', label: '入库' }, { value: 'out', label: '出库' }]} />
          <Select placeholder="来源" allowClear showSearch style={{ width: 180 }} value={source || undefined}
            onChange={v => setSource(v || '')}
            options={(stats.sources || []).map(s => ({ value: s, label: s }))} />
          <Select placeholder="操作人" allowClear showSearch style={{ width: 130 }} value={operator || undefined}
            onChange={v => setOperator(v || '')}
            options={(stats.operators || []).map(o => ({ value: o, label: o }))} />
          <RangePicker value={dateRange} onChange={setDateRange} allowClear />
          <Button type="primary" icon={<SearchOutlined />} onClick={applyFilter}>筛选</Button>
          <Button onClick={resetFilter}>重置</Button>
          <div style={{ flex: 1, textAlign: 'right', minWidth: 200 }}>
            <Space>
              <span style={{ color: '#8c8c8c', fontSize: 12 }}>共 {total} 条</span>
              <Button icon={<ReloadOutlined />} onClick={refresh}>刷新</Button>
              <Button icon={<DownloadOutlined />} loading={exporting} onClick={exportCsv}>导出 CSV</Button>
            </Space>
          </div>
        </Space>
      </Card>

      {/* ===== 详细流水表 ===== */}
      <Card size="small" style={{ borderRadius: 8 }} styles={{ body: { padding: 0 } }}>
        <Table
          size="middle"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={logs}
          scroll={{ x: 1240 }}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true, pageSizeOptions: [30, 50, 100],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); load(p, ps); },
          }}
        />
      </Card>

      {/* 图例说明 */}
      <div style={{ marginTop: 12, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <Space size={6}>
          <Tag color="green" icon={<ArrowUpOutlined />}>入库</Tag>
          <Tag color="red" icon={<ArrowDownOutlined />}>出库</Tag>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>数量正/负</span>
        </Space>
        <Space size={6}>
          <span style={{ color: '#CF1322', fontWeight: 700, background: '#FFF1F0', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>负余额</span>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>＝ 库存出问题时重点排查</span>
        </Space>
        <Space size={6}>
          <DatabaseOutlined style={{ color: '#8c8c8c' }} />
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>时间已换算为北京时间（原存储为 UTC）</span>
        </Space>
      </div>

      {/* ===== 负库存根因诊断弹窗 ===== */}
      <Modal
        title={<Space><BugOutlined style={{ color: '#CF1322' }} />负库存根因诊断</Space>}
        open={diagOpen}
        onCancel={() => setDiagOpen(false)}
        footer={null}
        width={780}
      >
        <div style={{ marginBottom: 14, fontSize: 13, color: '#595959', lineHeight: 1.7 }}>
          自动分析每个负库存商品的<b style={{ color: '#CF1322' }}>首次变负现场</b>、累计缺口和疑似原因，帮你快速定位「是哪笔操作 / 谁 / 扣了多少」导致变负。
        </div>

        {diagLoading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#8c8c8c' }}>诊断中…</div>
        ) : !diagData.length ? (
          <Empty description="当前没有负库存商品，库存健康" />
        ) : (
          <div style={{ maxHeight: '62vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, paddingRight: 4 }}>
            {diagData.map((d, idx) => renderDiagCard(d, idx))}
          </div>
        )}

        <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
          提示：诊断结果基于库存流水自动分析，实际库存以现场盘点为准。修正请在「库存管理 → 调整」里操作。
        </div>
      </Modal>

      {/* ===== 缇娜巡检报告弹窗 ===== */}
      <Modal
        title={<Space><RobotOutlined style={{ color: 'var(--brand)' }} />缇娜·库存巡检报告</Space>}
        open={auditOpen}
        onCancel={() => setAuditOpen(false)}
        footer={<Button onClick={() => setAuditOpen(false)}>关闭</Button>}
        width={780}
      >
        {auditLatest && (
          <>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
              巡检时间 <b style={{ fontFamily: 'monospace' }}>{auditLatest.time}</b>
              　·　触发方式：{auditLatest.trigger === 'auto' ? '每日自动' : '手动'}
              　·　推送微信：{auditLatest.pushed ? '已推送 ✅' : (auditLatest.count ? '未推送' : '无异常不推送')}
            </div>
            {!auditLatest.count ? (
              <Empty description="本次巡检未发现负库存，库存健康 ✅" />
            ) : (
              <>
                {auditLatest.narrative ? (
                  <div style={{ background: '#F6FFED', border: '1px solid #D9F7BE', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3F8600', marginBottom: 6 }}>🤖 AI 排查简报</div>
                    <div style={{ fontSize: 13, color: '#26201C', lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{auditLatest.narrative}</div>
                  </div>
                ) : (
                  <Alert type="warning" showIcon style={{ marginBottom: 14 }} message="AI 简析未生成（可能网络或接口异常），下面是结构化诊断结果" />
                )}
                <div style={{ fontSize: 12, fontWeight: 600, color: '#595959', marginBottom: 8 }}>结构化诊断明细</div>
                <div style={{ maxHeight: '46vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14, paddingRight: 4 }}>
                  {(auditLatest.items || []).map((d, idx) => renderDiagCard(d, idx))}
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* ===== 巡检历史弹窗 ===== */}
      <Modal
        title={<Space><HistoryOutlined />巡检历史</Space>}
        open={auditHistOpen}
        onCancel={() => setAuditHistOpen(false)}
        footer={null}
        width={680}
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={auditHistory}
          pagination={false}
          locale={{ emptyText: <Empty description="暂无巡检历史" /> }}
          columns={[
            { title: '巡检时间', dataIndex: 'time', width: 172, render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
            { title: '触发', dataIndex: 'trigger', width: 88, render: v => (v === 'auto' ? '每日自动' : '手动') },
            { title: '结果', dataIndex: 'count', width: 130, render: v => (v > 0 ? <Tag color="red">负库存 {v} 个</Tag> : <Tag color="green">正常</Tag>) },
            { title: '微信', dataIndex: 'pushed', width: 70, align: 'center', render: v => (v ? '✅' : '—') },
            {
              title: '', key: 'op', width: 70,
              render: (_, r) => <Button size="small" type="link" onClick={() => { setAuditLatest(r); setAuditOpen(true); }}>查看</Button>,
            },
          ]}
        />
      </Modal>
    </div>
  );
}
